import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Heart, Loader2, Coffee, Gift, Sparkles, Crown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const TIER_ICON = { coffee: Coffee, cheer: Gift, fan: Sparkles, super: Crown };

// Supports two modes:
//  • game tip  — pass { gameId, game }
//  • creator tip — pass { creatorId, creatorName, supporters, total } (used on the creator profile)
// `asButton` renders a rounded pill Button (profile header) instead of the inline pink text button.
export default function TipJar({ gameId, game, creatorId, creatorName, supporters = 0, total = 0, asButton = false, label = "Tip" }) {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const creatorMode = !gameId && !!creatorId;

  const loadSummary = () => {
    if (creatorMode) {
      setSummary({ creator_name: creatorName, supporters: Number(supporters) || 0, total: Number(total) || 0 });
    } else if (gameId) {
      api.get(`/tips/game/${gameId}`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
    }
  };
  useEffect(() => {
    loadSummary();
    api.get("/tips/tiers").then((r) => setTiers(r.data.tiers)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, creatorId, supporters, total]);

  // Handle return from Stripe checkout (?tip_session=...).
  useEffect(() => {
    const sid = params.get("tip_session");
    if (!sid) return;
    let tries = 0;
    const clear = () => { params.delete("tip_session"); setParams(params, { replace: true }); };
    const poll = async () => {
      try {
        const { data } = await api.get(`/tips/status/${sid}`);
        if (data.payment_status === "paid") {
          const perk = data.perk_credits || 0;
          toast.success(perk > 0
            ? `Thank you for supporting the creator! 💚 You earned +${perk} bonus AI credits.`
            : "Thank you for supporting the creator! 💚");
          clear(); loadSummary(); refresh?.(); return;
        }
        if (data.status === "expired") { clear(); return; }
      } catch { /* keep trying */ }
      if (tries++ < 5) setTimeout(poll, 2000);
      else clear();
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!summary) return null;
  const isOwner = creatorMode
    ? !!(user && user.id === creatorId)
    : !!(user && game && game.owner_id === user.id);

  const tip = async (tier) => {
    setBusy(tier);
    try {
      const body = creatorMode
        ? { creator_id: creatorId, tier, origin_url: window.location.origin, message: msg }
        : { game_id: gameId, tier, origin_url: window.location.origin, message: msg };
      const { data } = await api.post("/tips/checkout", body);
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start the tip"); setBusy(""); }
  };

  return (
    <>
      {asButton ? (
        <Button onClick={() => setOpen(true)} disabled={isOwner} data-testid="tip-jar-btn" variant="outline"
          title={isOwner ? "You can't tip yourself" : "Support this creator"}
          className="rounded-full border-pink-500/40 text-pink-500 hover:bg-pink-500/10 hover:text-pink-400 disabled:opacity-40">
          <Heart className="w-4 h-4 fill-pink-500" /> Support
          {summary.supporters > 0 && <span className="font-normal opacity-80" data-testid="tip-supporters">{summary.supporters}</span>}
        </Button>
      ) : (
        <button onClick={() => setOpen(true)} disabled={isOwner} data-testid="tip-jar-btn"
          title={isOwner ? "You can't tip your own game" : "Support this creator"}
          className="flex items-center gap-1.5 font-semibold text-pink-500 hover:text-pink-400 disabled:opacity-40 disabled:cursor-not-allowed">
          <Heart className="w-4 h-4 fill-pink-500" /> {label}
          {summary.supporters > 0 && (
            <span className="text-[hsl(var(--muted-foreground))] font-normal" data-testid="tip-supporters">{summary.supporters}</span>
          )}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500 fill-pink-500" /> Support {summary.creator_name || "this creator"}
            </DialogTitle>
            <DialogDescription>
              Send a one-tap tip to fuel more great games.
              {summary.supporters > 0
                ? ` ${summary.supporters} supporter${summary.supporters === 1 ? "" : "s"} · $${(summary.total || 0).toFixed(2)} tipped so far.`
                : " Be the first to tip! 🎉"}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {tiers.map((t) => {
              const Icon = TIER_ICON[t.id] || Heart;
              return (
                <Button key={t.id} onClick={() => tip(t.id)} disabled={!!busy} variant="outline"
                  className="h-auto py-3 flex-col gap-1 rounded-xl border-pink-500/30 hover:bg-pink-500/10" data-testid={`tip-tier-${t.id}`}>
                  {busy === t.id ? <Loader2 className="w-5 h-5 animate-spin text-pink-500" /> : <Icon className="w-5 h-5 text-pink-500" />}
                  <span className="font-extrabold text-base">${t.amount.toFixed(0)}</span>
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.label}</span>
                </Button>
              );
            })}
          </div>
          <Input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={200} placeholder="Add a message (optional)" data-testid="tip-message" />
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Secure checkout via Stripe — you'll return here right after paying.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
